import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Icon from '../../../components/dashboard/DashboardIcons';
import { PageHead, PrimaryBtn, inputCls } from '../finance/shared';
import { SESSION_STATUS_LABELS, SESSION_STATUS_COLORS, PLAN_TYPE_LABELS, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, StatusPill, StudioEmpty, Field, API, auth, fmtDate, errMsg } from '../../coach/studio/shared';
import { toast } from '../../../lib/toast';

const TABS = [
  { key: 'trainings', label: 'تدريبات الفرق' },
  { key: 'plans', label: 'الخطط المشاركة' },
  { key: 'reports', label: 'التقارير الفنية' },
];

export default function SharedCoachingHubPage() {
  const [tab, setTab] = useState('trainings');
  return (
    <div className="space-y-4" data-testid="shared-coaching-hub-page">
      <PageHead title="التدريب المشترك" desc="الجلسات والخطط والتقارير التي شاركها المدربون رسمياً مع المنشأة" />
      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="shared-coaching-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`shared-coaching-tab-${t.key}`}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === t.key ? 'bg-teal-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'trainings' && <TeamTrainingsTab />}
      {tab === 'plans' && <SharedPlansTab />}
      {tab === 'reports' && <SharedReportsTab />}
    </div>
  );
}

function TeamTrainingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/coach-studio/org/sessions`, auth());
        setItems(res.data);
      } catch (e) {
        setError('تعذر تحميل تدريبات الفرق');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = items.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  if (loading) return <div className="p-8 text-center text-sm text-slate-400" data-testid="team-trainings-loading">جاري التحميل...</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-500" data-testid="team-trainings-error">{error}</div>;
  if (items.length === 0) return <StudioEmpty text="لا توجد تدريبات مشاركة مع المنشأة حالياً" icon={<Icon name="field" className="w-6 h-6" />} />;

  return (
    <div className="space-y-3" data-testid="team-trainings-list">
      {Object.keys(grouped).sort().map((date) => (
        <div key={date} className="rounded-xl border border-slate-200 bg-white p-3">
          <h4 className="mb-2 text-xs font-bold text-slate-500">{fmtDate(date)}</h4>
          {grouped[date].map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 py-2 text-sm" data-testid={`team-training-row-${s.id}`}>
              <div>
                <span className="text-slate-700">{s.title}</span>
                <span className="mr-2 text-xs text-slate-400">— {s.coach_name || '—'} {s.team_name ? `· ${s.team_name}` : ''}</span>
              </div>
              <StatusPill status={s.status} labels={SESSION_STATUS_LABELS} colors={SESSION_STATUS_COLORS} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function SharedPlansTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/org/plans`, auth());
      setItems(res.data);
    } catch (e) {
      setError('تعذر تحميل الخطط المشاركة');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const review = async (planId, reviewType) => {
    let note = '';
    if (reviewType === 'changes_requested') {
      const input = window.prompt('ملاحظات على الخطة (اختياري):');
      if (input === null) return;
      note = input;
    }
    try {
      await axios.post(`${API}/coach-studio/org/plans/${planId}/review`, { review_type: reviewType, note }, auth());
      toast.success('تم إرسال المراجعة');
      load();
    } catch (e) {
      toast.error('تعذر إرسال المراجعة');
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400" data-testid="shared-plans-loading">جاري التحميل...</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-500" data-testid="shared-plans-error">{error}</div>;
  if (items.length === 0) return <StudioEmpty text="لا توجد خطط مشاركة مع منشأتك حالياً" icon={<Icon name="clipboard" className="w-6 h-6" />} />;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="shared-plans-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-right text-xs text-slate-500">
          <tr><th className="p-3">الخطة</th><th className="p-3">المدرب</th><th className="p-3">الفريق</th><th className="p-3">النوع</th><th className="p-3">الحالة</th><th className="p-3">إجراءات</th></tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.plan_id} className="border-t border-slate-100" data-testid={`shared-plan-row-${p.plan_id}`}>
              <td className="p-3 font-semibold text-slate-800">{p.name}<div className="text-xs text-slate-400">{fmtDate(p.start_date)} — {fmtDate(p.end_date)}</div></td>
              <td className="p-3 text-slate-600">{p.coach_name}</td>
              <td className="p-3 text-slate-600">{p.team_name || '—'}</td>
              <td className="p-3 text-slate-600">{PLAN_TYPE_LABELS[p.plan_type]}</td>
              <td className="p-3"><StatusPill status={p.status} labels={PLAN_STATUS_LABELS} colors={PLAN_STATUS_COLORS} /></td>
              <td className="p-3">
                {p.status === 'pending_approval' && (
                  <div className="flex gap-2">
                    <button onClick={() => review(p.plan_id, 'approved')} className="text-xs text-teal-700" data-testid={`shared-plan-approve-${p.plan_id}`}>اعتماد</button>
                    <button onClick={() => review(p.plan_id, 'changes_requested')} className="text-xs text-red-500" data-testid={`shared-plan-request-changes-${p.plan_id}`}>طلب تعديل</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return [start.toISOString().slice(0, 10), now.toISOString().slice(0, 10)];
}

function SharedReportsTab() {
  const [defFrom, defTo] = monthRange();
  const [dateFrom, setDateFrom] = useState(defFrom);
  const [dateTo, setDateTo] = useState(defTo);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/org/reports`, { ...auth(), params: { date_from: dateFrom, date_to: dateTo } });
      setReport(res.data);
    } catch (e) { setError(errMsg(e, 'تعذر تحميل التقرير')); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <Field label="من تاريخ"><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} data-testid="org-report-from-input" /></Field>
        <Field label="إلى تاريخ"><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} data-testid="org-report-to-input" /></Field>
        <PrimaryBtn onClick={load} data-testid="org-report-generate-btn"><Icon name="chart" className="w-4 h-4" /> عرض التقرير</PrimaryBtn>
      </div>
      {loading ? <div className="p-8 text-center text-sm text-slate-400" data-testid="org-report-loading">جاري التحميل...</div> :
        error ? <div className="p-8 text-center text-sm text-red-500" data-testid="org-report-error">{error}</div> :
        report && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="org-report-stats">
          <StatCard label="جلسات مشاركة" value={report.shared_sessions_total} testId="org-stat-total" />
          <StatCard label="مجدولة" value={report.sessions_scheduled} testId="org-stat-scheduled" />
          <StatCard label="مكتملة" value={report.sessions_completed} testId="org-stat-completed" />
          <StatCard label="مدربون مشاركون" value={report.coaches_involved} testId="org-stat-coaches" />
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
  </div>
);
